<template>
  <div class="response-page">
    <!-- Standalone header for response page -->
    <div class="response-page-header">
      <div class="flex-row flex-row--end">
        <img class="header__logo header__logo-clickable" src="../assets/img/decisionvue-logo.svg" alt="DecisionVue" @click="goToMain"/>
      </div>
      <div class="flex-row">
        <img class="header__logo center" src="../assets/img/wsp-logo.png" alt="WSP"/>
      </div>
      <div class="flex-row flex-row--end">
        <button class="language-toggle" @click="toggleLanguage" :title="currentLanguage === 'en' ? 'Switch to French' : 'Passer à l\'anglais'">
          {{ currentLanguage === 'en' ? 'FR' : 'EN' }}
        </button>
      </div>
    </div>
    
    <div class="response-container">
      <div class="response-card">
        <!-- Loading State -->
        <div v-if="isLoading" class="loading-section">
          <div class="loading-spinner"></div>
          <h3>{{ t.validatingRequest }}</h3>
          <p>{{ t.pleaseWait }}</p>
        </div>

        <!-- Unauthorized State -->
        <div v-else-if="tokenValid === false" class="error-section">
          <div class="error-icon">
            <i class="icon-warning"></i>
          </div>
          <div class="error-content">
            <h3>{{ t.unauthorizedAccess }}</h3>
            <p>{{ errorMessage || t.invalidToken }}</p>
            <p>{{ t.checkLink }}</p>
          </div>
          <div class="error-actions">
            <button class="btn-secondary" @click="goToMain">
              {{ t.goToLogin }}
            </button>
          </div>
        </div>

        <!-- Authorized Response Form -->
        <div v-else-if="tokenValid === true">
          <!-- Show new response form when user wants to submit another response -->
          <div v-if="showNewResponseForm" class="response-form">
            <div class="response-header">
              <h2>{{ t.submitAnotherResponse }}</h2>
              <p>{{ t.pleaseSelectResponse }}</p>
            </div>

            <div class="response-options">
              <button 
                class="response-btn response-btn--ok" 
                :class="{ 'active': selectedResponse === 'ok' }"
                @click="selectResponse('ok')"
              >
                <i class="icon-check"></i>
                {{ t.ok }}
              </button>
              
              <button 
                class="response-btn response-btn--not-ok" 
                :class="{ 'active': selectedResponse === 'not-ok' }"
                @click="selectResponse('not-ok')"
              >
                <i class="icon-close"></i>
                {{ t.notOk }}
              </button>
            </div>

            <!-- Message field for Not OK response -->
            <div v-if="selectedResponse === 'not-ok'" class="message-section">
              <label for="newResponseMessage" class="message-label">
                {{ t.provideDetails }}
              </label>
              <textarea 
                id="newResponseMessage"
                v-model="responseMessage"
                class="message-input"
                :placeholder="t.enterMessage"
                rows="4"
                maxlength="500"
              ></textarea>
              <div class="character-count">
                {{ responseMessage.length }}/500
              </div>
            </div>

            <!-- Error message display -->
            <div v-if="errorMessage" class="error-message">
              {{ errorMessage }}
            </div>

            <div class="response-actions">
              <button 
                class="btn-secondary" 
                @click="cancelNewResponse"
              >
                {{ t.cancel }}
              </button>
              <button 
                class="btn-primary" 
                @click="submitResponse"
                :disabled="!selectedResponse"
              >
                {{ t.submitResponse }}
              </button>
            </div>
          </div>

          <!-- Final Confirmation Screen -->
          <div v-else-if="submitted" class="confirmation-section">
            <div class="confirmation-icon">
              <i v-if="selectedResponse === 'ok'" class="icon-check-circle success"></i>
              <i v-else class="icon-info-circle warning"></i>
            </div>
            
            <div class="confirmation-content">
              <h3 v-if="selectedResponse === 'ok'">{{ t.responseConfirmed }}</h3>
              <h3 v-else>{{ t.responseRecorded }}</h3>
              
              <p v-if="selectedResponse === 'ok'">
                {{ t.okResponseSuccess }}
              </p>
              <p v-else>
                {{ t.yourNotOkResponse }}{{ responseMessage ? ' ' + t.and + ' message' : '' }} {{ t.notOkResponseSuccess }}
              </p>

              <div v-if="selectedResponse === 'not-ok' && responseMessage" class="response-summary">
                <strong>{{ t.yourMessage }}</strong>
                <div class="message-display">{{ responseMessage }}</div>
              </div>
            </div>

            <div class="confirmation-actions">
              <button class="btn-primary" @click="handleComplete">
                {{ t.exit }}
              </button>
              <button class="btn-secondary" @click="resetForm">
                {{ t.submitAnotherResponse }}
              </button>
            </div>
          </div>

          <!-- Initial Response Form (only shown if no initial response was provided) -->
          <div v-else-if="!initialResponseSubmitted" class="response-form">
            <div class="response-header">
              <h2>{{ t.responseRequired }}</h2>
              <p>{{ t.provideResponseToProceed }}</p>
            </div>

            <div class="response-options">
              <button 
                class="response-btn response-btn--ok" 
                :class="{ 'active': selectedResponse === 'ok' }"
                @click="selectResponse('ok')"
              >
                <i class="icon-check"></i>
                {{ t.ok }}
              </button>
              
              <button 
                class="response-btn response-btn--not-ok" 
                :class="{ 'active': selectedResponse === 'not-ok' }"
                @click="selectResponse('not-ok')"
              >
                <i class="icon-close"></i>
                {{ t.notOk }}
              </button>
            </div>

            <!-- Message field for Not OK response -->
            <div v-if="selectedResponse === 'not-ok'" class="message-section">
              <label for="responseMessage" class="message-label">
                {{ t.provideDetails }}
              </label>
              <textarea 
                id="responseMessage"
                v-model="responseMessage"
                class="message-input"
                :placeholder="t.enterMessage"
                rows="4"
                maxlength="500"
              ></textarea>
              <div class="character-count">
                {{ responseMessage.length }}/500
              </div>
            </div>

            <!-- Error message display -->
            <div v-if="errorMessage" class="error-message">
              {{ errorMessage }}
            </div>

            <div class="response-actions">
              <button 
                class="btn-secondary" 
                @click="goBack"
              >
                {{ t.cancel }}
              </button>
              <button 
                class="btn-primary" 
                @click="submitResponse"
                :disabled="!canSubmit"
              >
                {{ t.submitResponse }}
              </button>
            </div>
          </div>

          <!-- Initial Response Submitted - Add Message Form -->
          <div v-else class="message-form">
            <div class="response-header">
              <h2>{{ t.responseSubmitted }}</h2>
              <p v-if="selectedResponse === 'ok'">{{ t.okResponseRecorded }}</p>
              <p v-else>{{ t.notOkResponseRecorded }}</p>
            </div>

            <!-- Message form for not-ok responses -->
            <div v-if="selectedResponse === 'not-ok'" class="response-form">
              <div class="message-section">
                <label for="additionalMessage" class="message-label">
                  {{ t.addAdditionalDetails }}
                </label>
                <textarea 
                  id="additionalMessage"
                  v-model="responseMessage"
                  class="message-input"
                  :placeholder="t.enterAdditionalDetails"
                  rows="4"
                  maxlength="500"
                ></textarea>
                <div class="character-count">
                  {{ responseMessage.length }}/500
                </div>
              </div>

              <!-- Error message display -->
              <div v-if="errorMessage" class="error-message">
                {{ errorMessage }}
              </div>

              <div class="response-actions">
                <button 
                  class="btn-secondary" 
                  @click="goToMain"
                >
                  {{ t.skipMessage }}
                </button>
                <button 
                  class="btn-primary" 
                  @click="submitResponse"
                  :disabled="!responseMessage.trim()"
                >
                  {{ t.submitMessage }}
                </button>
              </div>
            </div>

            <!-- For OK responses, just show completion option -->
            <div v-else class="response-actions">
              <button 
                class="btn-primary" 
                @click="handleComplete"
              >
                {{ t.exit }}
              </button>
              <button 
                class="btn-secondary" 
                @click="resetForm"
              >
                {{ t.submitAnotherResponse }}
              </button>
            </div>
          </div>
        </div> <!-- End authorized response form section -->
      </div>
    </div>
  </div>
</template>

<script>
import RequestBuilder from '../js/requestBuilder';
const requestBuilder = new RequestBuilder();

export default {
  name: 'Response',
  data() {
    return {
      selectedResponse: null,
      responseMessage: '',
      submitted: false,
      submissionTime: null,
      token: null,
      initialResponse: null,
      tokenValid: null,
      isLoading: true,
      errorMessage: null,
      initialResponseSubmitted: false,
      canAddMessage: false,
      showNewResponseForm: false,
      currentLanguage: 'en' // Default to English
    }
  },
  mounted() {
    this.initializeFromQuery();
  },
  computed: {
    canSubmit() {
      // Comments are optional, so just check if a response is selected
      return !!this.selectedResponse;
    },
    serverRoute() {
      return this.$store.getters.serverRoute;
    },
    mctServerRoute() {
      return this.$store.getters.mctServerRoute;
    },
    t() {
      // Translation object based on current language
      const translations = {
        en: {
          validatingRequest: 'Validating Request...',
          pleaseWait: 'Please wait while we verify your access token.',
          unauthorizedAccess: 'Unauthorized Access',
          invalidToken: 'The provided token is invalid or has expired.',
          checkLink: 'Please check your link or contact support if you believe this is an error.',
          goToLogin: 'Go to Login',
          submitAnotherResponse: 'Submit Another Response',
          pleaseSelectResponse: 'Please select your new response',
          ok: 'OK',
          notOk: 'Not OK',
          provideDetails: 'Please provide details about the issue (optional):',
          enterMessage: 'Enter your message here...',
          cancel: 'Cancel',
          submitResponse: 'Submit Response',
          responseConfirmed: 'Response Confirmed',
          responseRecorded: 'Response and Message Recorded',
          okResponseSuccess: 'Your OK response has been successfully submitted.',
          notOkResponseSuccess: 'have been recorded. The appropriate team will be notified.',
          yourMessage: 'Your message:',
          exit: 'Done',
          responseRequired: 'Response Required',
          provideResponseToProceed: 'Please provide your response to proceed',
          responseSubmitted: 'Response Submitted',
          okResponseRecorded: 'Your OK response has been recorded.',
          notOkResponseRecorded: 'Your NOT OK response has been recorded. You can optionally add more details below.',
          addAdditionalDetails: 'Add additional details (optional):',
          enterAdditionalDetails: 'Enter additional details here...',
          skipMessage: 'Skip Message',
          submitMessage: 'Submit Message',
          and: 'and',
          yourNotOkResponse: 'Your NOT OK response'
        },
        fr: {
          validatingRequest: 'Validation de la demande...',
          pleaseWait: 'Veuillez patienter pendant que nous vérifions votre jeton d\'accès.',
          unauthorizedAccess: 'Accès non autorisé',
          invalidToken: 'Le jeton fourni est invalide ou a expiré.',
          checkLink: 'Veuillez vérifier votre lien ou contacter le support si vous pensez qu\'il s\'agit d\'une erreur.',
          goToLogin: 'Aller à la connexion',
          submitAnotherResponse: 'Soumettre une autre réponse',
          pleaseSelectResponse: 'Veuillez sélectionner votre nouvelle réponse',
          ok: 'OK',
          notOk: 'Pas OK',
          provideDetails: 'Veuillez fournir des détails sur le problème (optionnel):',
          enterMessage: 'Entrez votre message ici...',
          cancel: 'Annuler',
          submitResponse: 'Soumettre la réponse',
          responseConfirmed: 'Réponse confirmée',
          responseRecorded: 'Réponse et message enregistrés',
          okResponseSuccess: 'Votre réponse OK a été soumise avec succès.',
          notOkResponseSuccess: 'ont été enregistrés. L\'équipe appropriée sera notifiée.',
          yourMessage: 'Votre message:',
          exit: 'Terminé',
          responseRequired: 'Réponse requise',
          provideResponseToProceed: 'Veuillez fournir votre réponse pour continuer',
          responseSubmitted: 'Réponse soumise',
          okResponseRecorded: 'Votre réponse OK a été enregistrée.',
          notOkResponseRecorded: 'Votre réponse Pas OK a été enregistrée. Vous pouvez ajouter des détails supplémentaires ci-dessous.',
          addAdditionalDetails: 'Ajouter des détails supplémentaires (optionnel):',
          enterAdditionalDetails: 'Entrez des détails supplémentaires ici...',
          skipMessage: 'Ignorer le message',
          submitMessage: 'Soumettre le message',
          and: 'et',
          yourNotOkResponse: 'Votre réponse Pas OK'
        }
      };
      return translations[this.currentLanguage];
    }
  },
  methods: {
    async toggleLanguage() {
      const newLanguage = this.currentLanguage === 'en' ? 'fr' : 'en';
      
      // Update UI immediately for instant feedback
      this.currentLanguage = newLanguage;
      
      // Update language preference on server in background (don't await)
      if (this.token) {
        // Map our simple language code to the full language code expected by API
        const languageCode = newLanguage === 'en' ? 'en' : 'fr';
        
        requestBuilder.updateContactLanguage(
          this.mctServerRoute,
          this.token,
          languageCode
        ).then(async ([request, options]) => {
          const response = await fetch(request, options);
          
          if (response.ok) {
            const result = await response.json();
            console.log('Language preference updated on server:', result);
          } else {
            console.warn('Failed to update language preference on server');
          }
        }).catch(error => {
          console.error('Error updating language preference:', error);
        });
      }
    },
    async initializeFromQuery() {
      // Extract token and response from URL query parameters
      this.token = this.$route.query.token;
      this.initialResponse = this.$route.query.response;
      
      // Set language from URL parameter if provided (default to 'en')
      const urlLang = this.$route.query.lang;
      if (urlLang && (urlLang === 'en' || urlLang === 'fr')) {
        this.currentLanguage = urlLang;
      }
      
      if (!this.token) {
        this.errorMessage = 'Missing required token parameter';
        this.tokenValid = false;
        this.isLoading = false;
        return;
      }
      
      // Pre-select response from URL if provided; user will submit manually
      if (this.initialResponse) {
        this.selectedResponse = this.initialResponse;
      }

      this.tokenValid = true;
      this.isLoading = false;
    },


    selectResponse(response) {
      this.selectedResponse = response;
      if (response === 'ok') {
        this.responseMessage = '';
      }
    },
    async submitResponse() {
      console.log('Submitting response:', {
        showNewResponseForm: this.showNewResponseForm,
        selectedResponse: this.selectedResponse,
        hasMessage: !!this.responseMessage
      });
      
      try {
        this.isLoading = true;
        this.errorMessage = null;
        
        const comment = this.selectedResponse === 'not-ok' && this.responseMessage.trim() 
          ? this.responseMessage.trim() 
          : null;
        
        const [request, options] = await requestBuilder.submitMessageReply(
          this.mctServerRoute,
          this.token,
          this.selectedResponse,
          comment
        );
        
        const response = await fetch(request, options);
        const result = await response.json();
        
        if (response.ok) {
          console.log('Response submitted successfully:', result);
          this.submissionTime = new Date();
          this.submitted = true;
          this.canAddMessage = false;
          this.showNewResponseForm = false;
          this.errorMessage = null;
        } else {
          throw new Error(result.message || 'Failed to submit response');
        }
      } catch (error) {
        console.error('Error submitting response:', error);
        this.errorMessage = 'Failed to submit response. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.responseMessage = '';
      this.submitted = false;
      this.submissionTime = null;
      this.showNewResponseForm = true;
      this.selectedResponse = null;
      this.errorMessage = null;
    },

    cancelNewResponse() {
      this.showNewResponseForm = false;
      this.selectedResponse = this.initialResponse;
      this.responseMessage = '';
      this.errorMessage = null;
    },
    goBack() {
      this.$router.go(-1);
    },
    goToMain() {
      // Navigate to main map if authenticated, otherwise to login
      if (this.$store && this.$store.getters && this.$store.getters.currentUser) {
        this.$router.push({ name: 'map' });
      } else {
        this.$router.push({ name: 'login' });
      }
    },
    handleComplete() {
      // Close the current tab/window
      window.close();
    }
  }
}
</script>

<style scoped>
.response-page {
  min-height: 100vh;
  background: linear-gradient(135deg, var(--wood-grey) 0%, var(--wood-teal-dark) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.response-page-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: .75rem 1rem;
  color: white;
  line-height: 1;
  background-color: var(--wood-grey);
  border-bottom: 1px solid rgba(35, 56, 69, 0);
  box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
  font-size: 1rem;
  z-index: 1000;
}

.header__logo {
  height: 20px;
  width: auto;
}

.header__logo-clickable {
  cursor: pointer;
}

.flex-row {
  display: flex;
  align-items: center;
}

.flex-row--end {
  display: flex;
  align-items: flex-end;
}

.flex-row > * + * {
  margin-left: .5rem;
}

.language-toggle {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s ease;
  text-transform: uppercase;
}

.language-toggle:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.response-logo {
  height: 40px;
  width: auto;
}

.response-container {
  width: 100%;
  max-width: 600px;
  margin-top: 60px; /* Account for fixed header - adjusted for smaller header */
}

.response-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.response-header {
  background: var(--wood-grey);
  color: white;
  padding: 30px;
  text-align: center;
  margin: 8px;
}

.response-header h2 {
  margin: 0 0 10px 0;
  font-size: 24px;
  font-weight: 600;
}

.response-header p {
  margin: 0;
  opacity: 0.9;
  font-size: 16px;
}

.response-form,
.message-form,
.confirmation-section {
  padding: 40px;
}

.response-options {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  justify-content: center;
}

.response-btn {
  flex: 1;
  max-width: 200px;
  padding: 20px;
  border: 2px solid var(--grey2);
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 500;
}

.response-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.response-btn.active {
  border-color: var(--wood-teal);
  background: var(--wood-teal-light);
  color: var(--wood-teal);
}

.response-btn--ok.active {
  border-color: var(--wood-green);
  background: rgba(136, 197, 64, 0.1);
  color: var(--wood-green);
}

.response-btn--not-ok.active {
  border-color: var(--red);
  background: var(--light-red);
  color: var(--red);
}

.message-section {
  margin-bottom: 30px;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.message-label {
  display: block;
  margin-bottom: 10px;
  font-weight: 500;
  color: var(--wood-grey);
}

.message-input {
  width: 100%;
  padding: 15px;
  border: 2px solid var(--grey2);
  border-radius: 6px;
  resize: vertical;
  font-family: inherit;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.message-input:focus {
  outline: none;
  border-color: var(--wood-teal);
}

.character-count {
  text-align: right;
  font-size: 12px;
  color: var(--grey4);
  margin-top: 5px;
}

.response-actions,
.confirmation-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
}

.btn-primary,
.btn-secondary {
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-primary {
  background: var(--wood-teal);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--wood-teal-dark);
}

.btn-primary:disabled {
  background: var(--grey2);
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: var(--wood-grey);
  border: 1px solid var(--grey2);
}

.btn-secondary:hover {
  background: var(--grey1);
}

.confirmation-section {
  text-align: center;
}

.confirmation-icon {
  margin-bottom: 20px;
}

.confirmation-icon i {
  font-size: 60px;
}

.confirmation-icon .success {
  color: var(--wood-green);
}

.confirmation-icon .warning {
  color: var(--red);
}

.confirmation-content h3 {
  margin: 0 0 15px 0;
  color: var(--wood-grey);
  font-size: 20px;
}

.confirmation-content p {
  margin: 0 0 20px 0;
  color: var(--grey4);
  line-height: 1.5;
}

.response-summary {
  background: var(--grey1);
  padding: 20px;
  border-radius: 6px;
  text-align: left;
  margin: 20px 0;
}

.message-display {
  margin-top: 10px;
  padding: 10px;
  background: white;
  border-radius: 4px;
  border-left: 3px solid var(--wood-teal);
  font-style: italic;
}

/* Icons - you may need to adjust these based on your icon system */
.icon-check::before { content: "✓"; }
.icon-close::before { content: "✕"; }
.icon-check-circle::before { content: "✓"; }
.icon-info-circle::before { content: "ℹ"; }
.icon-warning::before { content: "⚠"; }

/* Loading styles */
.loading-section,
.error-section {
  padding: 60px 40px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--grey2);
  border-top: 4px solid var(--wood-teal);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-section h3,
.error-section h3 {
  margin: 0 0 15px 0;
  color: var(--wood-grey);
  font-size: 20px;
}

.loading-section p,
.error-section p {
  margin: 0 0 15px 0;
  color: var(--grey4);
  line-height: 1.5;
}

.error-section {
  border-top: 4px solid var(--red);
}

.error-icon {
  margin-bottom: 20px;
}

.error-icon i {
  font-size: 60px;
  color: var(--red);
}

.error-content {
  margin-bottom: 30px;
}

.error-actions {
  display: flex;
  justify-content: center;
}

.error-message {
  background: var(--light-red);
  color: var(--red);
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  border-left: 4px solid var(--red);
}

/* Responsive design */
@media (max-width: 768px) {
  .response-page {
    padding: 10px;
  }
  
  .response-options {
    flex-direction: column;
  }
  
  .response-btn {
    max-width: none;
  }
  
  .response-actions,
  .confirmation-actions {
    flex-direction: column;
  }
  
  .response-form,
  .confirmation-section {
    padding: 20px;
  }
}
</style>
